import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { toLocalIST, STATUS } from "./mk";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const dot = { draft: "bg-slate-400", approved: "bg-sky-500", scheduled: "bg-indigo-600", posting: "bg-amber-500", posted: "bg-emerald-600", failed: "bg-red-600" };
const pad = (n) => String(n).padStart(2, "0");

export default function ContentCalendar({ posts, onOpen, onNew }) {
  const ist = toLocalIST(new Date().toISOString());
  const now = { y: +ist.slice(0, 4), m: +ist.slice(5, 7) - 1, d: +ist.slice(8, 10) };
  const [ym, setYm] = useState({ y: now.y, m: now.m });
  const first = new Date(ym.y, ym.m, 1);
  const lead = (first.getDay() + 6) % 7;
  const days = new Date(ym.y, ym.m + 1, 0).getDate();
  const byDay = {};
  posts.forEach((p) => {
    const when = toLocalIST(p.posted_at || p.scheduled_at);
    if (when.startsWith(`${ym.y}-${pad(ym.m + 1)}`)) (byDay[+when.slice(8, 10)] ||= []).push(p);
  });
  const shift = (d) => setYm(({ y, m }) => { const t = new Date(y, m + d, 1); return { y: t.getFullYear(), m: t.getMonth() }; });
  const unplanned = posts.filter((p) => !p.scheduled_at && !p.posted_at).length;
  const today = now.y === ym.y && now.m === ym.m ? now.d : 0;

  return (
    <div data-testid="mk-calendar">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Button variant="outline" size="icon" onClick={() => shift(-1)} data-testid="mk-cal-prev"><ChevronLeft className="h-4 w-4" /></Button>
        <h3 className="text-lg font-bold text-slate-900 w-44 text-center" data-testid="mk-cal-month">{first.toLocaleString("en-GB", { month: "long", year: "numeric" })}</h3>
        <Button variant="outline" size="icon" onClick={() => shift(1)} data-testid="mk-cal-next"><ChevronRight className="h-4 w-4" /></Button>
        <div className="flex flex-wrap gap-3 ml-auto text-xs text-slate-600">
          {Object.keys(dot).filter((k) => k !== "posting").map((k) => <span key={k} className="flex items-center gap-1"><span className={`h-2 w-2 rounded-full ${dot[k]}`} />{STATUS[k].label}</span>)}
        </div>
      </div>
      {unplanned > 0 && <p className="text-xs text-slate-500 mb-2">{unplanned} draft(s) have no date yet — see Approvals.</p>}
      <div className="overflow-x-auto">
        <div className="grid grid-cols-7 gap-1.5 min-w-[640px]">
          {DAYS.map((d) => <div key={d} className="text-[11px] font-bold uppercase tracking-wider text-slate-500 px-1">{d}</div>)}
          {Array.from({ length: lead }).map((_, i) => <div key={`l${i}`} />)}
          {Array.from({ length: days }, (_, i) => i + 1).map((d) => (
            <div key={d} className={`group min-h-[96px] rounded-xl bg-white p-1.5 ring-1 ${d === today ? "ring-2 ring-blue-700" : "ring-slate-200"}`} data-testid={`mk-cal-day-${d}`}>
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-slate-700">{d}</span>
                <button onClick={() => onNew(`${ym.y}-${pad(ym.m + 1)}-${pad(d)}T10:00`)} className="opacity-60 sm:opacity-0 group-hover:opacity-100 focus:opacity-100 text-blue-700 transition-opacity" aria-label="New post" data-testid={`mk-cal-add-${d}`}><Plus className="h-3.5 w-3.5" /></button>
              </div>
              <div className="mt-1 space-y-1">
                {(byDay[d] || []).map((p) => (
                  <button key={p.id} onClick={() => onOpen(p)} className="w-full text-left flex items-center gap-1 rounded-md bg-slate-50 hover:bg-blue-50 px-1 py-0.5 text-[11px] text-slate-800 transition-colors" data-testid={`mk-cal-post-${p.id}`}>
                    <span className={`h-2 w-2 shrink-0 rounded-full ${dot[p.status]}`} /><span className="truncate">{p.title}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
